/**
 * TerraformVariablesEditor - Custom Backstage Scaffolder Field Extension
 *
 * A dynamic form editor that parses Terraform variable definitions and
 * generates appropriate input fields for each variable type.
 *
 * Usage in template.yaml:
 *   ui:field: TerraformVariablesEditor
 *   ui:options:
 *     terraformPath: '/path/to/terraform/module'
 *     # OR use a parameter reference:
 *     terraformPathParam: 'repoPath'
 *     tfvarsPath: '/path/to/terraform.tfvars'  # optional
 *     tfvarsPathParam: 'tfvarsPath'            # optional
 *     hideSensitive: false                      # optional
 *     collapseObjects: false                    # optional
 *     showDescriptions: true                    # optional
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  TerraformVariablesEditorOptions,
  TerraformVariableSchema,
  ParseTerraformResponse,
} from './types';
import { VariableEditor } from './VariableEditor';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    variableCard: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    variableHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    badge: {
      fontSize: '0.7rem',
    },
    section: {
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    warningAlert: {
      marginBottom: theme.spacing(2),
    },
    sourceInfo: {
      display: 'flex',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      flexWrap: 'wrap',
    },
    sourceChip: {
      fontSize: '0.75rem',
    },
  }),
);

type FormDataType = Record<string, unknown>;

export const TerraformVariablesEditor = (
  props: FieldExtensionComponentProps<FormDataType, TerraformVariablesEditorOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData = {},
    formContext,
    uiSchema,
    schema,
  } = props;

  // DEBUG: Log formData on every render to trace JSON string serialization
  console.log('[TerraformVariablesEditor] RENDER - formData:', JSON.stringify(
    Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [
        k,
        {
          type: typeof v,
          isString: typeof v === 'string',
          preview: typeof v === 'string' ? v.substring(0, 80) : (typeof v === 'object' ? 'object' : v),
        }
      ])
    ),
    null,
    2
  ));

  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const options = uiSchema?.['ui:options'] || {};
  const {
    terraformPath: staticTerraformPath,
    terraformPathParam,
    tfvarsPath: staticTfvarsPath,
    tfvarsPathParam,
    hideSensitive = false,
    // collapseObjects = false, // TODO: implement collapse functionality
    showDescriptions = true,
  } = options;

  // Resolve paths from form context if using parameter references
  const formValues = formContext?.formData as Record<string, unknown> | undefined;
  const terraformPath = terraformPathParam
    ? (formValues?.[terraformPathParam] as string)
    : staticTerraformPath;
  const tfvarsPath = tfvarsPathParam
    ? (formValues?.[tfvarsPathParam] as string)
    : staticTfvarsPath;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variables, setVariables] = useState<TerraformVariableSchema[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sourceFiles, setSourceFiles] = useState<{
    variablesFile?: string;
    tfvarsFile?: string;
  }>({});

  // Helper function to parse JSON strings into proper objects/arrays
  // This ensures data is properly typed before being passed to the scaffolder
  // Must be defined before fetchVariables and handleVariableChange that use it
  const parseJsonStrings = useCallback((value: unknown): unknown => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Try to parse JSON objects and arrays
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed);
          console.log('[TerraformVariablesEditor] parseJsonStrings: Parsed JSON string to object:', {
            key: 'unknown',
            originalPreview: trimmed.substring(0, 50),
            parsedType: typeof parsed,
          });
          return parsed;
        } catch {
          // Not valid JSON, return as-is
        }
      }
      // Handle the literal string "null" - convert to actual null
      if (trimmed === 'null') {
        console.log('[TerraformVariablesEditor] parseJsonStrings: Converted "null" string to null');
        return null;
      }
    }
    return value;
  }, []);

  // Fetch variables from backend
  const fetchVariables = useCallback(async () => {
    if (!terraformPath) {
      setVariables([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${backendUrl}/api/terraform-module-scaffolder/parse`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for Backstage backend authentication
          body: JSON.stringify({
            terraformPath,
            tfvarsPath,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to parse terraform files: ${response.status}`,
        );
      }

      const data: ParseTerraformResponse = await response.json();
      setVariables(data.variables);
      setWarnings(data.warnings || []);
      setSourceFiles(data.sourceFiles);

      // DEBUG: Log what backend returned
      console.log('[TerraformVariablesEditor] fetchVariables: Backend response - variables:', data.variables.map(v => ({
        name: v.name,
        type: v.type,
        currentValueType: typeof v.currentValue,
        currentValueIsString: typeof v.currentValue === 'string',
        currentValuePreview: typeof v.currentValue === 'string' ? v.currentValue.substring(0, 50) : 'not-string',
        defaultType: typeof v.default,
      })));

      console.log('[TerraformVariablesEditor] fetchVariables: parsing form data and variable defaults');

      // Initialize form data with current values or defaults
      // Parse JSON strings to ensure proper typing
      const initialData: Record<string, unknown> = {};
      let hasChanges = false;

      // First, parse any existing formData values that are JSON strings
      for (const [key, value] of Object.entries(formData)) {
        const parsed = parseJsonStrings(value);
        initialData[key] = parsed;
        if (parsed !== value) {
          hasChanges = true;
        }
      }

      // Then add any missing variables from the backend response
      for (const variable of data.variables) {
        if (initialData[variable.name] === undefined) {
          if (variable.currentValue !== undefined) {
            initialData[variable.name] = parseJsonStrings(variable.currentValue);
            hasChanges = true;
          } else if (variable.default !== undefined) {
            initialData[variable.name] = parseJsonStrings(variable.default);
            hasChanges = true;
          }
        }
      }

      // Update form data if any values changed
      if (hasChanges) {
        // DEBUG: Log what we're sending to onChange
        console.log('[TerraformVariablesEditor] fetchVariables: Calling onChange with initialData:', Object.fromEntries(
          Object.entries(initialData).map(([k, v]) => [
            k,
            { type: typeof v, isObject: typeof v === 'object' && v !== null, preview: typeof v === 'string' ? v.substring(0, 50) : 'not-string' }
          ])
        ));
        onChange(initialData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [terraformPath, tfvarsPath, backendUrl, formData, onChange, parseJsonStrings]);

  useEffect(() => {
    fetchVariables();
  }, [terraformPath, tfvarsPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVariableChange = useCallback(
    (variableName: string, value: unknown) => {
      // Parse JSON strings when values are changed through the UI
      const parsedValue = parseJsonStrings(value);
      console.log('[TerraformVariablesEditor] handleVariableChange:', {
        variableName,
        originalValue: typeof value === 'string' ? value.substring(0, 100) : value,
        parsedValue: typeof parsedValue === 'object' ? 'object' : parsedValue,
        valueType: typeof value,
        parsedType: typeof parsedValue,
      });
      onChange({
        ...formData,
        [variableName]: parsedValue,
      });
    },
    [formData, onChange, parseJsonStrings],
  );

  // Group variables by required/optional
  const requiredVars = variables.filter(v => v.required && !(hideSensitive && v.sensitive));
  const optionalVars = variables.filter(v => !v.required && !(hideSensitive && v.sensitive));
  const sensitiveVars = hideSensitive
    ? []
    : variables.filter(v => v.sensitive);

  if (!terraformPath) {
    return (
      <Box className={classes.root}>
        <Alert severity="info">
          Please provide a terraform path to load variable definitions.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 16 }}>
          Loading Terraform variables...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (variables.length === 0) {
    return (
      <Box className={classes.root}>
        <Alert severity="info">
          No variables found in the terraform module.
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h6">
          {schema.title || 'Terraform Variables'}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {variables.length} variable{variables.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {schema.description && showDescriptions && (
        <Typography
          variant="body2"
          color="textSecondary"
          paragraph
          style={{ marginBottom: 16 }}
        >
          {schema.description}
        </Typography>
      )}

      {/* Source file info */}
      <Box className={classes.sourceInfo}>
        {sourceFiles.variablesFile && (
          <Chip
            size="small"
            label={`Variables: ${sourceFiles.variablesFile}`}
            className={classes.sourceChip}
            variant="outlined"
          />
        )}
        {sourceFiles.tfvarsFile && (
          <Chip
            size="small"
            label={`Values: ${sourceFiles.tfvarsFile}`}
            className={classes.sourceChip}
            variant="outlined"
            color="primary"
          />
        )}
      </Box>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert severity="warning" className={classes.warningAlert}>
          <Typography variant="body2">Parsing warnings:</Typography>
          <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
            {warnings.map((warning, i) => (
              <li key={i}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Required Variables */}
      {requiredVars.length > 0 && (
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Typography variant="subtitle1">Required Variables</Typography>
            <Chip
              size="small"
              label={requiredVars.length}
              color="secondary"
              className={classes.badge}
            />
          </Box>
          {requiredVars.map(variable => (
            <Paper key={variable.name} className={classes.variableCard} variant="outlined">
              <VariableEditor
                variable={variable}
                value={formData[variable.name]}
                onChange={value => handleVariableChange(variable.name, value)}
              />
            </Paper>
          ))}
        </Box>
      )}

      {/* Optional Variables */}
      {optionalVars.length > 0 && (
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Typography variant="subtitle1">Optional Variables</Typography>
            <Chip
              size="small"
              label={optionalVars.length}
              className={classes.badge}
            />
          </Box>
          {optionalVars.map(variable => (
            <Paper key={variable.name} className={classes.variableCard} variant="outlined">
              <VariableEditor
                variable={variable}
                value={formData[variable.name]}
                onChange={value => handleVariableChange(variable.name, value)}
              />
            </Paper>
          ))}
        </Box>
      )}

      {/* Sensitive Variables */}
      {sensitiveVars.length > 0 && (
        <Box className={classes.section}>
          <Divider style={{ marginBottom: 16 }} />
          <Box className={classes.sectionTitle}>
            <Typography variant="subtitle1">Sensitive Variables</Typography>
            <Chip
              size="small"
              label={sensitiveVars.length}
              color="default"
              className={classes.badge}
            />
          </Box>
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            These variables are marked as sensitive. Values will be masked.
          </Alert>
          {sensitiveVars.map(variable => (
            <Paper key={variable.name} className={classes.variableCard} variant="outlined">
              <VariableEditor
                variable={variable}
                value={formData[variable.name]}
                onChange={value => handleVariableChange(variable.name, value)}
              />
            </Paper>
          ))}
        </Box>
      )}

      {/* Validation errors from scaffolder */}
      {rawErrors && rawErrors.length > 0 && (
        <Alert severity="error" style={{ marginTop: 16 }}>
          {rawErrors.join(', ')}
        </Alert>
      )}
    </Box>
  );
};

export default TerraformVariablesEditor;
